import type { IHttpResponse } from '@/store/definition';
import { $fetch } from 'ofetch';
type ErrorCallback = (status: number) => Promise<void>;
export interface IHttpClient {
  getData(
    uri: string,
    headersParam: any,
    queryParam: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse>;
  postData(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse>;
  putData(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse>;
  deleteData(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse>;
}

class HttpClient implements IHttpClient {
  /**
   * getメソッドを実行する
   *
   * @param {string} uri                           URI
   * @param {any} headersParam                     ヘッダパラメータ
   * @param {any} queryParam                       クエリパラメータ
   * @param {number} maxRetryCount                 最大リトライ回数
   * @param {boolean} requestTimeoutRetry          リクエストタイムアウトリトライ可否
   * @param {boolean} errorCodeRequestTimeoutRetry エラーリクエストタイムアウトリトライ可否
   * @param {any} errorCallback                    エラーコールバック関数
   * @param {string} key                           キー
   * @returns {IHttpResponse}                       HTTPレスポンス
   */
  async getData(
    uri: string,
    headersParam: any,
    queryParam: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number,
    key?: string
  ): Promise<IHttpResponse> {
    const retryCount = 0;
    let httpStatus: number = 0;
    // isCredentialを分離
    const { isCredential, ...headers } = headersParam || {};
    const response = await this.callFetch(
      uri,
      'GET',
      headers as HeadersInit,
      !!isCredential,
      queryParam,
      null,
      retryCount,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );
    let responseBody = null;
    if (response !== undefined) {
      responseBody = response.body;
      httpStatus = response.status;
    }
    const IHttpResponse: IHttpResponse = {
      status: httpStatus,
      body: responseBody,
    };

    return IHttpResponse;
  }

  /**
   * postメソッドを実行する
   *
   * @param {string} uri                           URI
   * @param {any} headersParam                     ヘッダパラメータ
   * @param {any} queryParam                       クエリパラメータ
   * @param {any} body                             ボディ
   * @param {number} maxRetryCount                 最大リトライ回数
   * @param {boolean} requestTimeoutRetry          リクエストタイムアウトリトライ可否
   * @param {boolean} errorCodeRequestTimeoutRetry エラーリクエストタイムアウトリトライ可否
   * @param {any} errorCallback                    エラーコールバック関数
   * @returns {IHttpResponse}                       HTTPレスポンス
   */
  async postData(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse> {
    const retryCount = 0;
    let httpStatus: number = 0;
    const { isCredential, ...headers } = headersParam || {};
    const response = await this.callFetch(
      uri,
      'POST',
      headers as HeadersInit,
      !!isCredential,
      queryParam,
      body,
      retryCount,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );
    let responseBody = null;
    if (response !== undefined) {
      responseBody = response.body;
      httpStatus = response.status;
    }
    const IHttpResponse: IHttpResponse = {
      status: httpStatus,
      body: responseBody,
    };

    return IHttpResponse;
  }

  /**
   * putメソッドを実行する
   *
   * @param {string} uri                           URI
   * @param {any} headersParam                     ヘッダパラメータ
   * @param {any} queryParam                       クエリパラメータ
   * @param {any} body                             ボディ
   * @param {number} maxRetryCount                 最大リトライ回数
   * @param {boolean} requestTimeoutRetry          リクエストタイムアウトリトライ可否
   * @param {boolean} errorCodeRequestTimeoutRetry エラーリクエストタイムアウトリトライ可否
   * @param {any} errorCallback                    エラーコールバック関数
   * @returns {IHttpResponse}                       HTTPレスポンス
   */
  async putData(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse> {
    const retryCount = 0;
    let httpStatus: number = 0;
    const { isCredential, ...headers } = headersParam || {};
    const response = await this.callFetch(
      uri,
      'PUT',
      headers as HeadersInit,
      !!isCredential,
      queryParam,
      body,
      retryCount,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );
    let responseBody = null;
    if (response !== undefined) {
      responseBody = response.body;
      httpStatus = response.status;
    }
    const IHttpResponse: IHttpResponse = {
      status: httpStatus,
      body: responseBody,
    };

    return IHttpResponse;
  }

  /**
   * deleteメソッドを実行する
   *
   * @param {string} uri                           URI
   * @param {any} headersParam                     ヘッダパラメータ
   * @param {any} queryParam                       クエリパラメータ
   * @param {any} body                             ボディ
   * @param {number} maxRetryCount                 最大リトライ回数
   * @param {boolean} requestTimeoutRetry          リクエストタイムアウトリトライ可否
   * @param {boolean} errorCodeRequestTimeoutRetry エラーリクエストタイムアウトリトライ可否
   * @param {any} errorCallback                    エラーコールバック関数
   * @returns {IHttpResponse}                       HTTPレスポンス
   */
  async deleteData(
    uri: string,
    headersParam: any,
    queryParam: any,
    body: any,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse> {
    const retryCount = 0;
    let httpStatus: number = 0;
    const { isCredential, ...headers } = headersParam || {};
    const response = await this.callFetch(
      uri,
      'DELETE',
      headers as HeadersInit,
      !!isCredential,
      queryParam,
      body,
      retryCount,
      maxRetryCount,
      requestTimeoutRetry,
      errorCodeRequestTimeoutRetry,
      errorCallback,
      timeout
    );
    let responseBody = null;
    if (response !== undefined) {
      responseBody = response.body;
      httpStatus = response.status;
    }
    const IHttpResponse: IHttpResponse = {
      status: httpStatus,
      body: responseBody,
    };

    return IHttpResponse;
  }

  async callFetch(
    uri: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    headers: HeadersInit,
    isCredential: boolean,
    param: Record<string, any>,
    body: Record<string, any> | BodyInit,
    retryCount: number,
    maxRetryCount: number,
    requestTimeoutRetry: boolean,
    errorCodeRequestTimeoutRetry: boolean,
    errorCallback: ErrorCallback,
    timeout: number
  ): Promise<IHttpResponse> {
    const controller = new AbortController();
    let httpStatus: number = 0;
    let responseData: IHttpResponse['body'] = null;
    let requestTimeout: boolean = false;
    let errorCodeRequestTimeout: boolean = false;
    const timer = setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      const response = await $fetch.raw(uri, {
        headers: headers,
        method: method,
        query: param,
        body: body,
        credentials: isCredential ? 'include' : 'same-origin',
        signal: controller.signal,
        ignoreResponseError: true,
      });

      httpStatus = response.status;
      responseData = response._data;

      if (400 <= httpStatus) {
        errorCodeRequestTimeout = true;
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        httpStatus = 408;
        requestTimeout = true;
      } else {
        httpStatus = 404;
      }
    } finally {
      clearTimeout(timer);
    }

    if (httpStatus == 404 || httpStatus == 408 || httpStatus == 490 || 500 <= httpStatus) {
      if (!requestTimeoutRetry && requestTimeout) {
        // リクエストタイムアウトはリトライしない かつ リクエストタイムアウトの場合
        if (httpStatus == 408) {
          // リトライ
          retryCount++;
        } else {
          // リトライ回数を最大にしてエラーコールバックを呼ぶ
          retryCount = Number.MAX_SAFE_INTEGER;
        }
      } else if (!errorCodeRequestTimeoutRetry && errorCodeRequestTimeout) {
        // エラーコードリクエストタイムアウトはリトライしない かつ エラーコードリクエストタイムアウトの場合

        // リトライ回数を最大にしてエラーコールバックを呼ぶ
        retryCount = Number.MAX_SAFE_INTEGER;
      } else {
        // リトライ
        retryCount++;
      }

      console.log(`retryCount --> ${retryCount} ( [${httpStatus}] ${uri} )`);
      if (retryCount > maxRetryCount) {
        console.log(`Retry limit exceeded. ( ${uri} )`);
        // 指定されたときのみ実行する
        if (errorCallback) {
          await errorCallback(httpStatus);
        }
        return { status: httpStatus, body: responseData };
      }
      console.log(`Retry(${retryCount}/${maxRetryCount}) ... ${uri}`);
      return await this.callFetch(
        uri,
        method,
        headers,
        isCredential,
        param,
        body,
        retryCount,
        maxRetryCount,
        requestTimeoutRetry,
        errorCodeRequestTimeoutRetry,
        errorCallback,
        timeout
      );
    } else if (400 <= httpStatus && httpStatus < 500) {
      // エラーコールバック: 指定されたときのみ実行する
      if (errorCallback) {
        await errorCallback(httpStatus);
      }
    }

    return { status: httpStatus, body: responseData };
  }
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      httpClient: new HttpClient(),
    },
  };
});
